/**-------------------------------  Copyright ---------------------------------
This software has been developed by Orange Labs TGI/OLS/SOFT
Copyright (c) Orange Labs 2018

COPYRIGHT    : This file is the property of Orange Labs.
Copyright Orange Labs 2018, All Rights Reserved

This software is the confidential and proprietary information of Orange Labs.

You shall not disclose such Confidential Information and shall use it only
in accordance with the terms of the license agreement you entered into with
Orange Labs.

AUTHORS      : Orange Labs  TGI/OLS/SOFT
EMAIL        : soft.cybersec@orange.com
-----------------------------------------------------------------------------*/


import {HTTPService} from '@/services/HTTPService'
import {AnalysisVO} from '@/valueObjects/AnalysisVO'
import {AxiosResponse} from 'axios'
import {DestinationHostVO} from '@/valueObjects/DestinationHostVO'
import {StreamVO} from '@/valueObjects/StreamVO'
import {HTTP_LONG_TIMEOUT} from '@/utils/constants'

export class AnalysisService {
    private url = '/analysis'

    constructor(private httpService: HTTPService) {
    }

    public async processAnalysis(file: any,
                                 virustotalRequested: boolean,
                                 pfavRequested: boolean,
                                 dynamicAnalysisRequested: boolean,
                                 internetRequested: boolean,
                                 analysisParameters: {[index: string]: string|string[]|boolean|number}): Promise<AnalysisVO> {
        const data = new FormData()
        data.append('file', file)

        this.buildDataWithAnalysisParameters(data, analysisParameters)

        const url = `${this.url}?virustotal_requested=${virustotalRequested}&dynamic_analysis_requested=${dynamicAnalysisRequested}&pfav_requested=${pfavRequested}&internet_requested=${internetRequested}`

        const result: AxiosResponse = await this.httpService.postMultipart(url, data)
        return result.data
    }
    public async updateAnalysis(analysisId: any,
                                virusTotalRequested: boolean,
                                pfavRequested: boolean,
                                yaraUpdateRequested: boolean,
                                analysisParameters: {[index: string]: string|string[]|boolean}): Promise<AnalysisVO> {
        const data = new FormData()

        this.buildDataWithAnalysisParameters(data, analysisParameters)

        const url = `${this.url}/${analysisId}?virustotal_requested=${virusTotalRequested}&yara_update_requested=${yaraUpdateRequested}`

        const result: AxiosResponse = await this.httpService.postMultipart(url, data)
        return result.data
    }

    public async getAnalysis(analysisId: number): Promise<[AnalysisVO, boolean]> {
        const analysis = await this.httpService.get(`${this.url}/${analysisId}`)
        let isFinished: boolean = false
        if (analysis.status === 200) {
            isFinished = true
        }
        return [AnalysisVO.fromServiceFormat(analysis.data), isFinished]
    }


    public async getAnalyses(page: number, count: number, searchString: string): Promise<AnalysisVO[]> {
        let url = this.url + '?page_number=' + page + '&items_per_page=' + count
        if (searchString !== '') {
            url += '&search_string=' + searchString
        }
        const analyses = await this.httpService.get(url)
        let isFinished: boolean = false
        if (analyses.status === 200) {
            isFinished = true
        }
        return analyses.data.map(AnalysisVO.fromServiceFormat)
    }

    public async deleteAnalysis(analysis: AnalysisVO): Promise<void> {
        await this.httpService.patch(`${this.url}/${analysis.id}`, {trash: true})
    }

    public async getArtefacts(analysisId: number): Promise<Blob> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/archive`, {timeout: HTTP_LONG_TIMEOUT, responseType: 'blob'})
        const file = new Blob([result.data])
        return file
    }

    public async getStaticArtefacts(analysisId: number): Promise<Blob> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/staticarchive`, {timeout: HTTP_LONG_TIMEOUT, responseType: 'blob'})
        const file = new Blob([result.data])
        return file
    }

    public async getDynamicArtefacts(analysisId: number): Promise<Blob> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/dynamicarchive`, {timeout: HTTP_LONG_TIMEOUT, responseType: 'blob'})
        const file = new Blob([result.data])
        return file
    }

    public async getAPKArtefacts(analysisId: number): Promise<Blob> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/apk`, {timeout: HTTP_LONG_TIMEOUT, responseType: 'blob'})
        const file = new Blob([result.data])
        return file
    }

    public async getAPKIcon(analysisId: number, icon_path: string): Promise<Blob> {
        let url = `${this.url}/${analysisId}/apk_icon?icon_path=${icon_path}`
        const file = await this.httpService.get(url)
        return file.data
    }

    public async hideIcon(analysisId: number): Promise<void> {
        let url = `${this.url}/${analysisId}/hide_apk_icon`
        const data = new FormData()
        await this.httpService.postMultipart(url,data)
    }

    public async getLightReport(analysisId: number): Promise<Blob> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/light_report`, {timeout: HTTP_LONG_TIMEOUT, responseType: 'blob'})
        const file = new Blob([result.data])
        return file
    }

    public async getAnalysisLogs(analysisId: number): Promise<string> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/artefacts/dynamic/syslog`, {responseType: 'text'})
        return result.data
    }

    public async downloadAnalysisLogs(analysisId: number): Promise<Blob> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/artefacts/dynamic/syslog`, {responseType : 'blob'})
        const file = new Blob([result.data], {type: 'application/text'})
        return file
    }

    public async getAnalysisHook(analysisId: number): Promise<string> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/artefacts/dynamic/hook`, {responseType: 'text'})
        return result.data
    }

    public async downloadAnalysisHook(analysisId: number): Promise<Blob> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/artefacts/dynamic/hook`, {responseType : 'blob'})
        const file = new Blob([result.data], {type: 'application/text'})
        return file
    }

    public async downloadAnalysisNetwork(analysisId: number): Promise<Blob> {
        const result = await this.httpService
                                 .get(`${this.url}/${analysisId}/artefacts/dynamic/networkdumps/traces`, {responseType : 'blob'})
        const file = new Blob([result.data], {type: 'application/octet-stream'})
        return file
    }

    public async getAnalysisNetworkActivity(analysisId: number): Promise<DestinationHostVO[]> {
        const destinationHostsResult = await this.httpService.get(`${this.url}/${analysisId}/network/activities`)
        return destinationHostsResult.data.map(DestinationHostVO.fromServiceFormat)
    }

    public async getHostActivity(analysisId: number, hostIp: string): Promise<StreamVO[]> {
        const streamsResult = await this.httpService.get(`${this.url}/${analysisId}/network/activities/${hostIp}`)
        return streamsResult.data.map(StreamVO.fromServiceFormat)
    }

    public async stopInteractiveSession(analysis: AnalysisVO): Promise<void> {
        await this.httpService.patch(`${this.url}/${analysis.id}`, {stop_interactive: true})
    }

    public isNotEmpty(value: string | string[] | boolean | number) {
        return !(
            (Array.isArray(value) && value.length === 0)
            ||
            (typeof(value) === 'string' && !value)
        )

    }

    private buildDataWithAnalysisParameters(data: FormData, analysisParameters: {[index: string]: string|string[]|boolean|number}) {
        for (const parameter of Object.keys(analysisParameters)) {
            const value = analysisParameters[parameter]
            if (this.isNotEmpty(value)) {
                data.append(parameter, value.toString())
            }
        }
    }
}
